import React, { useState, useEffect } from 'react';
import { loadUsers } from '../api/index.js';

const NewClientForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    cell: '',
    specialty: '',
    email: '',
    country: 'México',
    googleAdsLink: '',
    googleMapsLink: '',
    facebookLink: '',
    metaAccountLink: '',
    salesExecutive: ''
  });

  const [errors, setErrors] = useState({});
  const [showCustomSpecialtyInput, setShowCustomSpecialtyInput] = useState(false);
  const [customSpecialty, setCustomSpecialty] = useState('');
  const [selectedExecutives, setSelectedExecutives] = useState([]);
  const [availableExecutives, setAvailableExecutives] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);

  // Cargar ejecutivos y especialidades desde la base de datos
  useEffect(() => {
    const loadExecutives = async () => {
      try {
        console.log('Cargando ejecutivos desde la API...');
        const users = await loadUsers();
        console.log('Usuarios recibidos:', users);
        
        // Filtrar solo usuarios que tienen células asignadas
        const executives = users.filter(user => user.cells && user.cells.length > 0);
        console.log('Ejecutivos filtrados:', executives);
        
        setAvailableExecutives(executives);
      } catch (error) {
        console.error('Error cargando ejecutivos:', error);
        console.error('Error details:', error.message);
      }
    };

    const loadSpecialties = async () => {
      try {
        setLoadingSpecialties(true);
        console.log('🔄 Cargando especialidades desde la API (con sincronización automática)...');
        const response = await fetch('http://localhost:3001/api/specialties');
        if (!response.ok) {
          throw new Error('Error al cargar especialidades');
        }
        const specialtiesData = await response.json();
        console.log('✅ Especialidades recibidas:', specialtiesData.length, 'especialidades');
        console.log('📋 Incluye especialidades de clientes existentes:', 
          specialtiesData.filter(s => !s.is_default).length, 'personalizadas');
        setSpecialties(specialtiesData);
      } catch (error) {
        console.error('❌ Error cargando especialidades:', error);
        // En caso de error, usar especialidades predeterminadas
        setSpecialties([
          { name: 'Medicina General', is_default: true },
          { name: 'Cardiología', is_default: true },
          { name: 'Dermatología', is_default: true },
          { name: 'Endocrinología', is_default: true },
          { name: 'Gastroenterología', is_default: true },
          { name: 'Ginecología y Obstetricia', is_default: true },
          { name: 'Neurología', is_default: true },
          { name: 'Oncología', is_default: true },
          { name: 'Oftalmología', is_default: true },
          { name: 'Ortopedia y Traumatología', is_default: true },
          { name: 'Otorrinolaringología', is_default: true },
          { name: 'Pediatría', is_default: true },
          { name: 'Psiquiatría', is_default: true },
          { name: 'Psicología', is_default: true },
          { name: 'Reumatología', is_default: true },
          { name: 'Urología', is_default: true },
          { name: 'Neumología', is_default: true },
          { name: 'Cirugía General', is_default: true },
          { name: 'Cirugía Laparoscópica', is_default: true },
          { name: 'Cirugía Plástica y Reconstructiva', is_default: true },
          { name: 'Cirugía Cardiovascular', is_default: true },
          { name: 'Cirugía Pediátrica', is_default: true },
          { name: 'Cirugía Oncológica', is_default: true },
          { name: 'Cirugía Coloproctológica', is_default: true },
          { name: 'Cirugía Maxilofacial', is_default: true },
          { name: 'Medicina Interna', is_default: true },
          { name: 'Nefrología', is_default: true },
          { name: 'Hematología', is_default: true },
          { name: 'Medicina Física y Rehabilitación', is_default: true },
          { name: 'Anestesiología', is_default: true },
          { name: 'Medicina Familiar', is_default: true },
          { name: 'Infectología', is_default: true },
          { name: 'Radiología', is_default: true },
          { name: 'Endodoncia (odontología especializada)', is_default: true },
          { name: 'Odontología (Dentista)', is_default: true },
          { name: 'Nutriología', is_default: true },
          { name: 'Terapia Física', is_default: true },
          { name: 'Medicina del Deporte', is_default: true },
          { name: 'Toxicología', is_default: true },
          { name: 'Genética Médica', is_default: true },
          { name: 'Fisioterapia', is_default: true },
          { name: 'Terapia Ocupacional', is_default: true },
          { name: 'Logopedia (Terapia del lenguaje)', is_default: true }
        ]);
      } finally {
        setLoadingSpecialties(false);
      }
    };
    
    loadExecutives();
    loadSpecialties();
  }, []);

  // Mostrar todos los ejecutivos disponibles (no filtrar por célula)
  const getFilteredExecutives = () => {
    return availableExecutives;
  };

  const handleExecutiveToggle = (executive) => {
    const executiveEmail = executive.email;
    const isSelected = selectedExecutives.includes(executiveEmail);
    
    if (isSelected) {
      const newSelected = selectedExecutives.filter(email => email !== executiveEmail);
      setSelectedExecutives(newSelected);
      setFormData(prev => ({ 
        ...prev, 
        salesExecutive: newSelected.join(', '),
        salesExecutives: newSelected
      }));
    } else {
      const newSelected = [...selectedExecutives, executiveEmail];
      setSelectedExecutives(newSelected);
      setFormData(prev => ({ 
        ...prev, 
        salesExecutive: newSelected.join(', '),
        salesExecutives: newSelected
      }));
    }
  };

  const removeExecutive = (executiveToRemove) => {
    const newSelected = selectedExecutives.filter(email => email !== executiveToRemove);
    setSelectedExecutives(newSelected);
    setFormData(prev => ({ 
      ...prev, 
      salesExecutive: newSelected.join(', '),
      salesExecutives: newSelected
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'specialty' && value === 'custom') {
      setShowCustomSpecialtyInput(true);
      setFormData(prev => ({ ...prev, specialty: '' }));
    } else {
      setShowCustomSpecialtyInput(false);
      setCustomSpecialty('');
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // No limpiar ejecutivos al cambiar célula - son independientes
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };


  const handleCustomSpecialtyChange = (e) => {
    setCustomSpecialty(e.target.value);
    setFormData(prev => ({ ...prev, specialty: e.target.value }));
  };

  const validateEmail = (email) => {
    if (!email) return true;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Nombre es requerido';
    
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Ingrese un email válido';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Si hay una especialidad personalizada, crearla primero
    if (showCustomSpecialtyInput && customSpecialty.trim()) {
      try {
        console.log('Creando nueva especialidad:', customSpecialty.trim());
        const response = await fetch('http://localhost:3001/api/specialties', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: customSpecialty.trim()
          }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Especialidad creada exitosamente:', result);
          
          // IMPORTANTE: Recargar TODAS las especialidades para obtener la lista actualizada
          // Esto asegura que el sistema automático del backend funcione correctamente
          console.log('🔄 Recargando lista completa de especialidades...');
          try {
            const refreshResponse = await fetch('http://localhost:3001/api/specialties');
            if (refreshResponse.ok) {
              const updatedSpecialties = await refreshResponse.json();
              console.log('✅ Lista actualizada:', updatedSpecialties.length, 'especialidades');
              setSpecialties(updatedSpecialties);
            }
          } catch (refreshError) {
            console.error('Error refrescando especialidades:', refreshError);
            // Fallback: agregar solo la nueva especialidad
            setSpecialties(prev => [...prev, { 
              id: result.id, 
              name: result.name, 
              is_default: false 
            }]);
          }
        } else if (response.status === 409) {
          // La especialidad ya existe, no es un error
          console.log('⚠️ La especialidad ya existe, continuando...');
        } else {
          const error = await response.json();
          console.error('❌ Error creando especialidad:', error);
          // Continuar con la creación del cliente aunque falle la especialidad
        }
      } catch (error) {
        console.error('Error al crear especialidad:', error);
        // Continuar con la creación del cliente aunque falle la especialidad
      }
    }
    
    // Preparar datos del cliente asegurando que salesExecutive no sea null
    const clientDataToSave = {
      ...formData,
      salesExecutive: formData.salesExecutive || '', // Asegurar que no sea null
      salesExecutives: selectedExecutives.length > 0 ? selectedExecutives : [] // Asegurar que sea un array
    };
    
    onSave(clientDataToSave);
  };


  const countries = [
    'México', 'Estados Unidos', 'Canadá', 'España', 'Colombia', 'Perú', 'Ecuador', 'Panamá',
    'República Dominicana', 'El Salvador', 'Chile', 'Honduras', 'Guatemala', 'Belice', 'Paraguay',
    'Uruguay', 'Bolivia'
  ];

  const cells = [
    '', 'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4',
    'G7M1', 'G7M2', 'G7M3', 'G7M4'
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-[#0061A8] mb-4">Nuevo Cliente</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Nombre completo <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`mt-1 block w-full border ${errors.name ? 'border-red-500' : 'border-[#D1D3D4]'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]`}
            required
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Célula
          </label>
          <select
            name="cell"
            value={formData.cell}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          >
            {cells.map(cell => (
              <option key={cell} value={cell}>{cell === '' ? 'Seleccione una célula' : cell}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Especialidad
          </label>
          <select
            name="specialty"
            value={showCustomSpecialtyInput ? 'custom' : formData.specialty}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            disabled={loadingSpecialties}
          >
            <option value="">
              {loadingSpecialties ? 'Cargando especialidades...' : 'Seleccione una especialidad'}
            </option>
            {specialties.map((spec, index) => (
              <option key={spec.id || index} value={spec.name}>
                {spec.name} {!spec.is_default && '(Personalizada)'}
              </option>
            ))}
            <option value="custom">Otra (especificar)</option>
          </select>
          {showCustomSpecialtyInput && (
            <div className="mt-2">
              <input
                type="text"
                value={customSpecialty}
                onChange={handleCustomSpecialtyChange}
                placeholder="Especifique especialidad"
                className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              />
              <p className="text-xs text-gray-500 mt-1">
                Esta especialidad se agregará automáticamente para futuros clientes
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Ej. contacto@clinica.com"
            className={`mt-1 block w-full border ${errors.email ? 'border-red-500' : 'border-[#D1D3D4]'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]`}
          />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            País
          </label>
          <select
            name="country"
            value={formData.country}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          >
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>

        {/* Nuevos campos de datos del cliente */}
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Google Ads
          </label>
          <input
            type="url"
            name="googleAdsLink"
            value={formData.googleAdsLink}
            onChange={handleChange}
            placeholder="https://ads.google.com/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Google Maps
          </label>
          <input
            type="url"
            name="googleMapsLink"
            value={formData.googleMapsLink}
            onChange={handleChange}
            placeholder="https://maps.app.goo.gl/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Facebook
          </label>
          <input
            type="url"
            name="facebookLink"
            value={formData.facebookLink}
            onChange={handleChange}
            placeholder="https://facebook.com/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Cuenta de Meta
          </label>
          <input
            type="url"
            name="metaAccountLink"
            value={formData.metaAccountLink}
            onChange={handleChange}
            placeholder="https://business.facebook.com/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[#63666A] mb-3">
            Ejecutivos de Ventas
          </label>
          
          {/* Lista de checkboxes para ejecutivos */}
          <div className="border border-[#D1D3D4] rounded-md p-4 max-h-60 overflow-y-auto bg-gray-50">
            {getFilteredExecutives().length === 0 ? (
              <div className="text-gray-500 text-sm text-center py-4">
                No hay ejecutivos disponibles
              </div>
            ) : (
              <div className="space-y-3">
                {getFilteredExecutives().map((executive) => {
                  const executiveName = `${executive.firstName} ${executive.lastName}`;
                  const isSelected = selectedExecutives.includes(executive.email);
                  
                  return (
                    <label
                      key={executive.email}
                      className="flex items-start space-x-3 cursor-pointer hover:bg-white p-2 rounded-md transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleExecutiveToggle(executive)}
                        className="h-4 w-4 text-[#0061A8] focus:ring-[#0061A8] border-gray-300 rounded mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{executiveName}</div>
                        <div className="text-sm text-gray-500">{executive.email}</div>
                        <div className="text-xs text-gray-400">
                          Células: {executive.cells.join(', ')}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Mostrar resumen de ejecutivos seleccionados */}
          {selectedExecutives.length > 0 && (
            <div className="mt-3 p-3 bg-blue-50 rounded-md">
              <div className="text-sm font-medium text-blue-900 mb-2">
                {selectedExecutives.length} ejecutivo(s) seleccionado(s):
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedExecutives.map((executiveEmail, index) => {
                  // Encontrar el ejecutivo por email para mostrar su nombre
                  const executive = availableExecutives.find(exec => exec.email === executiveEmail);
                  const displayName = executive ? `${executive.firstName} ${executive.lastName}` : executiveEmail;
                  
                  return (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-[#0061A8] text-white"
                    >
                      {displayName}
                      <button
                        type="button"
                        onClick={() => removeExecutive(executiveEmail)}
                        className="ml-1 text-white hover:text-gray-200"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
          >
            Guardar Cliente
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewClientForm;
